import { type JSX } from 'react';

import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * One tile of a KPI band. `onClick` turns the tile into a real <button> —
 * keyboard-focusable and announced as a button — rather than a div with a
 * click handler, so a drill-down is reachable without a mouse.
 */
export interface KpiItem {
  label: string;
  value: string;
  sub?: string;
  onClick?: () => void;
  /** Native tooltip + accessible name for the clickable variant. */
  hint?: string;
}

export function KpiTile({ item }: { item: KpiItem }): JSX.Element {
  const body = (
    <>
      <div className="text-[11px] uppercase tracking-wide text-text-4">{item.label}</div>
      <div className="text-[24px] font-semibold tabular-nums text-text mt-1.5 leading-none">{item.value}</div>
      {item.sub && <div className="text-[11px] text-text-4 mt-1.5">{item.sub}</div>}
    </>
  );
  if (!item.onClick) {
    return <Card className="p-4">{body}</Card>;
  }
  return (
    <Card padding="none" className="transition-colors hover:border-volt-500/60">
      <button
        type="button"
        onClick={item.onClick}
        title={item.hint}
        aria-label={item.hint ?? item.label}
        className="w-full h-full text-left p-4 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-volt-500"
      >
        {body}
      </button>
    </Card>
  );
}

/**
 * The band itself: a responsive grid of KpiTile. Callers pass the large
 * breakpoint's column count through `className` (e.g. `lg:grid-cols-6`) so
 * the two ops pages can hold a different number of tiles with one component.
 */
export function KpiGrid({ items, className }: { items: KpiItem[]; className?: string }): JSX.Element {
  return (
    <div className={cn('grid grid-cols-2 sm:grid-cols-3 gap-2.5', className)}>
      {items.map((it) => (
        <KpiTile key={it.label} item={it} />
      ))}
    </div>
  );
}
